import { supabase } from '../lib/supabase'

export interface SendcloudShippingMethodSetting {
  shipping_method_id: number
  name: string
  carrier: string | null
  is_enabled: boolean
  updated_at: string
}

/** Fallback allowlist when settings cannot be loaded (matches historical hardcoded IDs). */
export const FALLBACK_BASE_SHIPPING_METHOD_IDS = [25013, 8229, 68, 16, 53]
export const BELGIUM_ONLY_SHIPPING_METHOD_ID = 3366

const ENABLED_IDS_CACHE_TTL_MS = 60_000
let enabledIdsCache: {
  fetchedAt: number
  ids: number[]
} | null = null

export function getFallbackAllowedShippingMethodIds(countryCode: string): number[] {
  return countryCode.toUpperCase() === 'BE'
    ? [...FALLBACK_BASE_SHIPPING_METHOD_IDS, BELGIUM_ONLY_SHIPPING_METHOD_ID]
    : [...FALLBACK_BASE_SHIPPING_METHOD_IDS]
}

function filterEnabledIdsForCountry(
  enabledIds: number[],
  countryCode: string,
): number[] {
  const isBelgium = countryCode.toUpperCase() === 'BE'
  return enabledIds.filter(
    (id) => id !== BELGIUM_ONLY_SHIPPING_METHOD_ID || isBelgium,
  )
}

async function loadEnabledShippingMethodIdsFromDb(): Promise<number[]> {
  const { data, error } = await supabase
    .from('sendcloud_shipping_method_settings')
    .select('shipping_method_id, is_enabled')
    .eq('is_enabled', true)

  if (error) {
    console.error('Error fetching enabled shipping methods:', error)
    throw error
  }

  if (!data || data.length === 0) {
    return []
  }

  return data.map(
    (row: { shipping_method_id: number }) => row.shipping_method_id,
  )
}

/**
 * Returns enabled method IDs for the given country.
 * Always applies the BE-only rule for method 3366 in code.
 * On query failure, falls back to the historical hardcoded allowlist.
 */
export async function getEnabledShippingMethodIds(
  countryCode: string,
): Promise<number[]> {
  try {
    const now = Date.now()
    let enabledIds: number[]

    if (
      enabledIdsCache &&
      now - enabledIdsCache.fetchedAt < ENABLED_IDS_CACHE_TTL_MS
    ) {
      enabledIds = enabledIdsCache.ids
    } else {
      enabledIds = await loadEnabledShippingMethodIdsFromDb()
      enabledIdsCache = { fetchedAt: now, ids: enabledIds }
    }

    if (enabledIds.length === 0) {
      return getFallbackAllowedShippingMethodIds(countryCode)
    }

    return filterEnabledIdsForCountry(enabledIds, countryCode)
  } catch (err) {
    console.error('Error fetching enabled shipping methods:', err)
    return getFallbackAllowedShippingMethodIds(countryCode)
  }
}

export async function getAllShippingMethodSettings(): Promise<
  SendcloudShippingMethodSetting[]
> {
  const { data, error } = await supabase
    .from('sendcloud_shipping_method_settings')
    .select('*')
    .order('name')

  if (error) {
    console.error('Error fetching shipping method settings:', error)
    throw error
  }

  return (data || []) as SendcloudShippingMethodSetting[]
}

export async function setShippingMethodEnabled(
  shippingMethodId: number,
  enabled: boolean,
): Promise<void> {
  const { error } = await supabase
    .from('sendcloud_shipping_method_settings')
    .update({ is_enabled: enabled, updated_at: new Date().toISOString() })
    .eq('shipping_method_id', shippingMethodId)

  if (error) {
    console.error('Error updating shipping method setting:', error)
    throw error
  }
}
